import axiosInstance from "../../lib/axiosInstance";
import { toast } from "react-toastify";
import { AppDispatch } from "../store";
import { packageUnitActions } from "../slices/packageUnit";
import { getErrorMessage } from "../../utils/errorUtils";
import { PackageUnitRequestDto } from "../../shared/dtos/packageUnit/PackageUnitRequestDto";
import { PackageUnitResponseDto } from "../../shared/dtos/packageUnit/PackageUnitResponseDto";
import { UpdatePackageUnitPayload } from "../../shared/interfaces/packageUnit/UpdatePackageUnitPayload";
import { PaginationQueryDto } from "../../shared/dtos/common/PaginationQueryDto";
import { ApiResponseDto } from "../../shared/dtos/common/ApiResponseDto";
import { PaginatedResponse } from "../../shared/dtos/common/PaginatedResponse";
import { ApiResponseStatus } from "../../shared/enums/ApiResponseStatus";

export const getPackageUnits =
  (params?: PaginationQueryDto) =>
  async (dispatch: AppDispatch): Promise<void> => {
    dispatch(packageUnitActions.setLoadingFetchAll());
    try {
      const { data } = await axiosInstance.get<
        ApiResponseDto<PaginatedResponse<PackageUnitResponseDto>>
      >("/package-units", {
        headers: { requiresAuth: true },
        params,
      });
      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        dispatch(packageUnitActions.setItems(data.data.content));
        dispatch(packageUnitActions.setPagination(data.data.pagination));
      }
    } catch (error) {
      dispatch(packageUnitActions.setErrorFetchAll(getErrorMessage(error)));
    }
  };

export const createPackageUnit =
  (values: PackageUnitRequestDto) =>
  async (dispatch: AppDispatch): Promise<void> => {
    dispatch(packageUnitActions.setLoadingCreate());
    try {
      const { data } = await axiosInstance.post<
        ApiResponseDto<PackageUnitResponseDto>
      >("/package-units", values, {
        headers: { requiresAuth: true },
      });
      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        toast.success(data.message || "Unit created successfully!");
        dispatch(packageUnitActions.createSuccess(data.data));
        dispatch(
          packageUnitActions.setServerStatus({
            operation: "create",
            status: data.statusCode || 201,
          }),
        );
      }
    } catch (error) {
      dispatch(packageUnitActions.setErrorCreate(getErrorMessage(error)));
    }
  };

export const updatePackageUnit =
  (payload: UpdatePackageUnitPayload) =>
  async (dispatch: AppDispatch): Promise<void> => {
    dispatch(packageUnitActions.setLoadingUpdate());
    try {
      const { data } = await axiosInstance.put<
        ApiResponseDto<PackageUnitResponseDto>
      >(`/package-units/${payload.id}`, payload.values, {
        headers: { requiresAuth: true },
      });
      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        toast.success(data.message || "Unit updated successfully!");
        dispatch(packageUnitActions.updateSuccess(data.data));
        dispatch(
          packageUnitActions.setServerStatus({
            operation: "update",
            status: data.statusCode || 200,
          }),
        );
      }
    } catch (error) {
      dispatch(packageUnitActions.setErrorUpdate(getErrorMessage(error)));
    }
  };

export const deletePackageUnit =
  (id: string) =>
  async (dispatch: AppDispatch): Promise<void> => {
    dispatch(packageUnitActions.setLoadingDelete());
    try {
      const { data } = await axiosInstance.delete<ApiResponseDto<void>>(
        `/package-units/${id}`,
        {
          headers: { requiresAuth: true },
        },
      );
      if (data.status === ApiResponseStatus.SUCCESS) {
        toast.success(data.message || "Unit deleted successfully!");
        dispatch(packageUnitActions.deleteSuccess(id));
      }
    } catch (error) {
      dispatch(packageUnitActions.setErrorDelete(getErrorMessage(error)));
    }
  };

export const resetPackageUnitServerStatus =
  () =>
  (dispatch: AppDispatch): void => {
    dispatch(packageUnitActions.setServerStatus(null));
  };
